const API_BASE = "https://api.mariamlyluxe.com/public/api";

// Zone API functions
export const zoneAPI = {
  // Get all zones (public)
  async getZones() {
    try {
      const response = await fetch(`${API_BASE}/zones`, {
        headers: {
          Accept: "application/json",
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching zones:", error);
      throw error;
    }
  },

  // Get shipping price for address
  async getShippingPrice(addressId) {
    try {
      const response = await fetch(`${API_BASE}/zones/shipping-price`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ address_id: addressId }),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching shipping price:", error);
      throw error;
    }
  },

  // Create zone (admin only)
  async createZone(zoneData, token) {
    try {
      const response = await fetch(`${API_BASE}/zones`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(zoneData),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error creating zone:", error);
      throw error;
    }
  },

  // Update zone (admin only)
  async updateZone(zoneId, zoneData, token) {
    try {
      const response = await fetch(`${API_BASE}/zones/${zoneId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(zoneData),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating zone:", error);
      throw error;
    }
  },

  // Delete zone (admin only)
  async deleteZone(zoneId, token) {
    try {
      const response = await fetch(`${API_BASE}/zones/${zoneId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting zone:", error);
      throw error;
    }
  },
};

// Address API functions
export const addressAPI = {
  // Get user addresses
  async getAddresses(token) {
    try {
      const response = await fetch(`${API_BASE}/addresses`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching addresses:", error);
      throw error;
    }
  },

  // Create address
  async createAddress(addressData, token) {
    try {
      const response = await fetch(`${API_BASE}/addresses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(addressData),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error creating address:", error);
      throw error;
    }
  },

  // Update address
  async updateAddress(addressId, addressData, token) {
    try {
      const response = await fetch(`${API_BASE}/addresses/${addressId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(addressData),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating address:", error);
      throw error;
    }
  },

  // Delete address
  async deleteAddress(addressId, token) {
    try {
      const response = await fetch(`${API_BASE}/addresses/${addressId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting address:", error);
      throw error;
    }
  },
};

// Cart API functions with shipping
export const cartAPI = {
  // Get cart with optional shipping calculation
  async getCart(token, addressId = null) {
    try {
      const url = addressId ? `${API_BASE}/cart?address_id=${addressId}` : `${API_BASE}/cart`;
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching cart:", error);
      throw error;
    }
  },

  // Add item to cart
  async addToCart(productId, quantity, token) {
    try {
      const response = await fetch(`${API_BASE}/cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: quantity,
        }),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  },

  // Update cart item quantity
  async updateCartItem(itemId, quantity, token) {
    try {
      const response = await fetch(`${API_BASE}/cart/update/${itemId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quantity }),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating cart item:", error);
      throw error;
    }
  },

  // Remove item from cart
  async removeFromCart(itemId, token) {
    try {
      const response = await fetch(`${API_BASE}/cart/remove/${itemId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error removing from cart:", error);
      throw error;
    }
  },
};

// Checkout API functions
export const checkoutAPI = {
  // Process checkout with shipping
  async processCheckout(checkoutData, token) {
    try {
      const response = await fetch(`${API_BASE}/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(checkoutData),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error processing checkout:", error);
      throw error;
    }
  },
};

export default {
  zoneAPI,
  addressAPI,
  cartAPI,
  checkoutAPI,
};
